import React from "react";
import { object, shape, string, arrayOf, oneOf, any, elementType } from "prop-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroLead from "@insightfulscience/atomic-react/Hero/Lead";
import HeroVerticalDelimiter from "@insightfulscience/atomic-react/Hero/VerticalDelimiter";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import UpdatesPageTemplate from "@insightfulscience/atomic-react/UpdatesPageTemplate";
import UpdatesPageTemplateTitle from "@insightfulscience/atomic-react/UpdatesPageTemplate/Title";
import UpdatesPageTemplateList from "@insightfulscience/atomic-react/UpdatesPageTemplate/List";
import UpdatesPageTemplateListItem from "@insightfulscience/atomic-react/UpdatesPageTemplate/Item";
import UpdatesPageTemplatePagination from "@insightfulscience/atomic-react/UpdatesPageTemplate/Pagination";
import Line from "@insightfulscience/atomic-react/Line";
import Heading from "@insightfulscience/atomic-react/Heading";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import { BackToTopButton } from "@insightfulscience/atomic-react/r2/BackToTopButton";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale } from "@insightfulscience/smart-react/i18";
import NextLink from "@insightfulscience/smart-react/NextLink";
import Link from "@insightfulscience/atomic-react/Link";
import HeroBrand from "../../ui/HeroBrand";
import PagePagination from "../../ui/PagePagination";
import richTextTypes from "../../richTextTypes/default";
import { BRAND_CODE_GP, PUBLISHED, BRAND_CODE_OM, BRAND_CODE_SG } from "../../constants";
import heroConfig from "./components/SectionHero/heroConfig";

const UpdatesPage = ({ page, pageProps }) => {
	const [t] = useLocale("updates");
	const { siteDomain, brandCode } = pageProps;
	const { latestVersion = {} } = page;
	const { pagination = null } = page;
	const { releaseNotes = [] } = page;
	const hoverColor = brandCode === BRAND_CODE_SG ? "3" : "white";

	return (
		<>
			<PageMeta title={t("META_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("META_TITLE")}
					description={t("META_DESCRIPTION")}
					url={`${siteDomain}/updates`}
				/>
				<PageMeta.Canonical href={`${siteDomain}/updates`} />
			</PageMeta>
			<HeroBrand brandCode={brandCode} config={heroConfig}>
				<HeroRow>
					<HeroCol>
						<HeroTitle>{t("HERO.PRISM_UPDATES")}</HeroTitle>
						<HeroLead sx={{ fontSize: "xl" }}>
							{t("HERO.THE_LATEST_PRISM_VERSION_IS")} {latestVersion?.version}
							{brandCode === BRAND_CODE_GP ? <> (Windows {t("HERO.AND")} Mac)</> : null}.
						</HeroLead>
					</HeroCol>
				</HeroRow>
				{brandCode === BRAND_CODE_GP || brandCode === BRAND_CODE_SG ? (
					<>
						<HeroRow>
							<HeroCol sm={12} md={8} sx={{ textAlign: "center" }}>
								<Line opacity={20} spacing={{ mt: 6, mb: 8 }} />
								<Heading sx={{ fontSize: "l", fontColor: "white" }}>
									{t("HERO.HOW_TO_UPDATE_TO_PRISM")} {latestVersion?.majorVersion}
								</Heading>
								<Paragraph sx={{ fontSize: "l", fontWeight: "medium" }}>
									{t("HERO.SUBSCRIBERS")}
								</Paragraph>
								<Paragraph sx={{ fontSize: "l", fontColor: "white--80" }}>
									{t("HERO.UPDATE_FOR_FREE")} (
									<Link color="4" hoverColor={hoverColor} href={t("HERO.INSTRUCTION_LINK")}>
										{t("HERO.INSTRUCTION_TITLE")}
									</Link>
									):
								</Paragraph>
								<Paragraph sx={{ fontSize: "l", textAlign: "center" }} spacing={{ my: 0 }}>
									<Link color="4" hoverColor={hoverColor} href={latestVersion?.windowsInstallerUrl}>
										Windows
									</Link>
									<HeroVerticalDelimiter />
									<Link color="4" hoverColor={hoverColor} href={latestVersion?.macInstallerUrl}>
										Mac
									</Link>

									{brandCode === BRAND_CODE_SG && (
										<>
											<HeroVerticalDelimiter />
											<Link
												color="4"
												hoverColor={hoverColor}
												href={latestVersion?.redHatInstallerUrl}
											>
												RedHat
											</Link>
											<HeroVerticalDelimiter />
											<Link
												color="4"
												hoverColor={hoverColor}
												href={latestVersion?.ubuntuInstallerUrl}
											>
												Ubuntu
											</Link>
										</>
									)}
								</Paragraph>
							</HeroCol>
						</HeroRow>
						<HeroRow>
							<HeroCol sm={12} md={8} sx={{ textAlign: "center" }}>
								<Paragraph sx={{ fontSize: "l", fontWeight: "medium" }}>
									{t("HERO.LICENSE_CUSTOMERS")}
								</Paragraph>
								<Paragraph sx={{ fontSize: "l", fontColor: "white--80" }}>
									{t("HERO.CONTACT_US_AT")} &nbsp;
									<Link color="4" hoverColor={hoverColor} href={t("HERO.CONTACT_LINK_HREF")}>
										{t("HERO.CONTACT_LINK_TITLE")}
									</Link>
									&nbsp; {t("HERO.TO_LEARN_ABOUT_UPGRADE_OPTIONS")}.
								</Paragraph>
							</HeroCol>
						</HeroRow>
					</>
				) : null}
			</HeroBrand>
			<UpdatesPageTemplate>
				{brandCode !== BRAND_CODE_OM && (
					<UpdatesPageTemplateTitle>{t("UPDATES.LATEST_RELEASES")}</UpdatesPageTemplateTitle>
				)}
				<UpdatesPageTemplateList>
					{releaseNotes.map(item => {
						const releaseDate = new Date(item.fields.releaseDate);

						return (
							<UpdatesPageTemplateListItem key={item.fields.title}>
								<NextLink
									Component={UpdatesPageTemplateListItem.Title}
									href={`/updates/${item.fields.slug}`}
								>
									{t("PRODUCT_NAME")} {item.fields?.appVersionNumber || ""}
								</NextLink>
								<UpdatesPageTemplateListItem.Date suppressHydrationWarning>
									{t("{{releaseDate, LONGDATE}}", { releaseDate })}
								</UpdatesPageTemplateListItem.Date>
								{item.fields.teaser ? (
									<UpdatesPageTemplateListItem.Description>
										{documentToReactComponents(item.fields.teaser, richTextTypes)}
									</UpdatesPageTemplateListItem.Description>
								) : null}
								<NextLink
									Component={UpdatesPageTemplateListItem.Action}
									href={`/updates/${item.fields.slug}`}
								>
									{t("UPDATES.DETAILS")}
								</NextLink>
							</UpdatesPageTemplateListItem>
						);
					})}
				</UpdatesPageTemplateList>
				{pagination ? (
					<UpdatesPageTemplatePagination>
						<PagePagination {...pagination} />
					</UpdatesPageTemplatePagination>
				) : null}
			</UpdatesPageTemplate>
			<BackToTopButton />
		</>
	);
};

UpdatesPage.propTypes = {
	page: shape({
		latestVersion: shape({
			version: string,
			majorVersion: string,
			macInstallerUrl: string,
			windowsInstallerUrl: string,
		}),
		releaseNotes: arrayOf(
			shape({
				appReleaseNotes: object, // eslint-disable-line react/forbid-prop-types
				appVersionNumber: string,
				body: object, // eslint-disable-line react/forbid-prop-types
				macInstallerUrl: string,
				publishStatus: oneOf(["Hidden", PUBLISHED]),
				releaseDate: string,
				slug: string,
				teaser: object, // eslint-disable-line react/forbid-prop-types
				title: string,
				windowsInstallerUrl: string,
			}),
		),
	}),
	pageProps: shape({
		resources: any, // eslint-disable-line react/forbid-prop-types
		siteDomain: string.isRequired,
		PageComponent: elementType,
	}),
};

export default UpdatesPage;
